/**
 * useApiHealth — RESIL-M1
 *
 * Hook React que expone el estado de salud de las APIs de geocodificación.
 * Llama a ApiHealthService (caché 30 min) al montar el componente.
 * Resultado: { hasCriticalIssues, endpoints, loading }
 *
 * @version 1.0.0
 */
import { useState, useEffect } from 'react'
import {
  fetchApiHealth,
  fetchEndpointDrift,
  type ApiEndpointHealth,
} from '../services/geocoding/ApiHealthService'

// ─── Types ────────────────────────────────────────────────────────────────────

/** Tipos de evento detectados por RESIL-WFS-S0 */
export type DriftEventType = 'MOVED' | 'DEAD' | 'LAYER_RENAMED' | 'LAYER_CHANGE' | 'RESURRECTED'

export interface DriftEvent {
  type: DriftEventType
  endpointId: string
  url?: string
  fromUrl?: string
  toUrl?: string
  layerMap?: { from: string; to: string } | { from: string; to: string }[]
  added?: string[]
  removed?: string[]
  ptelRelevant: boolean
  detectedAt: string
}

export interface ApiHealthState {
  /** true mientras se espera la respuesta del Worker */
  loading: boolean
  /** true si al menos una API crítica (DERA_g12 o CartoCiudad) está caída */
  hasCriticalIssues: boolean
  /** Lista completa de endpoints con su estado */
  endpoints: ApiEndpointHealth[]
  /** RESIL-WFS-S0: true si el Worker detectó drift en el último ciclo */
  driftDetected: boolean
  /** RESIL-WFS-S0: eventos de drift activos (MOVED/DEAD/RESURRECTED) */
  driftChanges: DriftEvent[]
  /** SONDEO-APP: hora (ISO, UTC) del sondeo de `/health/apis`; `null` si no llegó */
  healthTs: string | null
  /** SONDEO-APP: hora (ISO, UTC) del sondeo de `/health/endpoint-drift`; `null` si no llegó */
  driftTs: string | null
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useApiHealth(): ApiHealthState {
  const [state, setState] = useState<ApiHealthState>({
    loading: true,
    hasCriticalIssues: false,
    endpoints: [],
    driftDetected: false,
    driftChanges: [],
    healthTs: null,
    driftTs: null,
  })

  useEffect(() => {
    let cancelled = false

    Promise.all([fetchApiHealth(), fetchEndpointDrift()]).then(([health, drift]) => {
      if (cancelled) return
      setState({
        loading: false,
        hasCriticalIssues: health?.hasCriticalIssues ?? false,
        endpoints: health?.endpoints ?? [],
        driftDetected: drift?.driftDetected ?? false,
        driftChanges: (drift?.events?.filter(e => e.ptelRelevant) ?? []) as DriftEvent[],
        healthTs: health?.ts ?? null,
        driftTs: drift?.ts ?? null,
      })
    })

    return () => { cancelled = true }
  }, [])

  return state
}
